import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  SimpleDataTable as DataTable,
  Loader,
  ButtonOBSOLETED,
  Notification,
  Message
} from '../../web.common';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { Filters, FilterInput, FilterSelect, FilterTimePicker } from './Filters';
import { getQueryStringParameters } from '../../common/utils';
import { crawlingStatus } from '../../enums/crawlingStatus';
import {
  DisplayDate,
  DisplayGuid,
  DisplayStatus
} from '../items';
import AddEntityButton from '../misc/AddEntityButton';

const resultsCount = 15;

class CrawlingsInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isLoading: false };
  }

  goToPage(number) {
    // TODO consider to move responsability to filter component
    const { history, location } = this.props;
    const filters = Object.assign(
      {},
      getQueryStringParameters(location.search),
      {
        page: number
      });
    const search = Object.keys(filters)
      .filter(key => key && filters[key])
      .map(key => `${key}=${encodeURIComponent(filters[key])}`).join('&');
    history.push(`?${search}`);
  }

  async loadData(filters, skipPages) {
    const { props: { api: { crawlings: { searchCrawlings } } } } = this;

    this.setState({ isLoading: true });
    let result = null;

    try {
      result = await searchCrawlings({ filters, skipPages, resultsCount });
    }
    catch (e) {
      console.error(`unable to load. ${e}`);
      this.setState({ isLoading: false, errorMessage: new Message('error', 'Unable to load data') });

      return;
    }

    this.setState({ isLoading: false });

    return result;
  }

  render() {
    const { history, location } = this.props;
    const { isLoading, errorMessage } = this.state;
    const parameters = getQueryStringParameters(location.search);
    const pageNumber = parameters.page ? parseInt(parameters.page, 10) : 1;

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <Notification newMessage={errorMessage} />
            <PageTop
              documentTitle='Crawlings list'
              documentDescription='Crawlings list'
            />
            <h2 className='main-page-header'>
              Crawlings
              <AddEntityButton to='/crawling' />
            </h2>
            <Filters
              initFromUrl
              submitUrl
              validateForm={({
                crawlingId,
                productId,
                executingAt,
                status,
                afterAt,
                beforeAt
              }, updatedKey) => {
                if (updatedKey !== 'crawlingId') {
                  return {
                    productId,
                    executingAt,
                    status,
                    afterAt,
                    beforeAt
                  };
                }
                return { crawlingId };
              }}
              render={(formData, update) => (
                <div className='filters-block'>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='crawlingId'>
                      Id
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name='crawlingId'
                      placeholder='Find by id'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='productId'>
                      Product Id
                    </label>
                    <FilterInput
                      data={formData}
                      update={update}
                      name='productId'
                      placeholder='Filter by product id'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='afterAt'>
                      From
                    </label>
                    <FilterTimePicker
                      className='filter-date-picker'
                      data={formData}
                      update={update}
                      name='afterAt'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='beforeAt'>
                      To
                    </label>
                    <FilterTimePicker
                      className='filter-date-picker'
                      data={formData}
                      update={update}
                      name='beforeAt'
                    />
                  </div>
                  <div className='filters-label-block'>
                    <label className='filters-label' htmlFor='status'>
                      Status
                    </label>
                    <FilterSelect
                      className='filter-select'
                      data={formData}
                      update={update}
                      name='status'
                      options={crawlingStatus}
                    />
                  </div>
                  <br />
                  <br />
                  <div className='filters-submit-wrapper'>
                    <ButtonOBSOLETED type='submit' buttonContent='Search' disabled={isLoading} />
                  </div>
                  <hr />
                </div>
              )}
            />
            <DataTable
              style={{ marginTop: '100px' }}
              loadingContent={(<Loader hasLoaded={false} />)}
              tableConfig={{
                tableName: 'crawlings',
                rowClasses: 'hover-listview',
                rowClick: ({ id }) => {
                  history.push(`/crawling/${id}`);
                },
                columns: [
                  {
                    property: 'id',
                    label: 'Id',
                    render: ({ id }) => <DisplayGuid>{id}</DisplayGuid>
                  }, {
                    property: 'productName',
                    label: 'ProductName',
                    type: 'string'
                  }, {
                    property: 'facilityName',
                    label: 'FacilityName',
                    type: 'string'
                  }, {
                    property: 'systemName',
                    label: 'SystemName',
                    type: 'string'
                  },
                  {
                    property: 'status',
                    label: 'Status',
                    render: ({ status }) => <DisplayStatus mapping={crawlingStatus}>{status}</DisplayStatus>
                  }, {
                    property: 'createdAt',
                    label: 'Created At',
                    render: ({ createdAt }) => <DisplayDate>{createdAt}</DisplayDate>
                  }, {
                    // Started At replaces Executed At (same value for successful crawls); Executed
                    // At is only shown for crawls recorded before Started At existed.
                    property: 'startedAt',
                    label: 'Started At',
                    render: ({ startedAt, executedAt }) => startedAt
                      ? <DisplayDate>{startedAt}</DisplayDate>
                      : <span title='Executed At (legacy)'><DisplayDate>{executedAt}</DisplayDate> *</span>
                  }, {
                    property: 'durationSeconds',
                    label: 'Duration (s)',
                    render: ({ durationSeconds }) => durationSeconds != null ? durationSeconds : '—'
                  }
                ]
              }}
              getData={async newPageNumber => {
                const filters = getQueryStringParameters(location.search);
                return this.loadData(filters, newPageNumber || 1);
              }}
              paginationData={{
                renderLocation: 'both',
                rowsPerPage: resultsCount,
                pageNumber,
                goToPageNumber: number => this.goToPage(number)
              }}
              skipTableNoResults
              cellVerticalAlign='top'
              hideTableHead={false}
              languageCode='en'
              labels={{
                tableBody: {
                  noResult: <div style={{ display: 'block', textAlign: 'center' }}>No Results</div>
                }
              }}
              cellContent='cellContent'
            />
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const Crawlings = withRouter(withApi(connect(({ user }) => ({ isLoggedIn: !!user }), {})(CrawlingsInternal)));

Crawlings.requiresLogin = true;
Crawlings.propTypes = { isLoggedIn: PropTypes.bool };

export default Crawlings;